import React, { useState, useEffect, useContext } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { LanguageCode, getLanguageCodeFromKey } from '@/i18n/languageCodesToKey';
import { useMediaQuery } from '@react-hook/media-query';
import { MOBILE_MEDIA_QUERY } from 'utils/constants';
import { correctLocaleCasing } from 'utils/correctLocaleCasing';
import { buildPublicPathFromSlug } from 'utils/publicUrlRule';
import { PageContext } from '@/context/PageContext';
import { Icon } from '../Icon/Icon';
import { Popover } from '../Popover/Popover';
import styles from './TranslationMenu.module.scss';

interface Props {
  fullsize?: boolean;
  slug: string[];
  languages: string[];
}
export function TranslationMenu(props: Props): React.ReactElement | null {
  const { fullsize, slug, languages } = props;
  const { linkHostname } = useContext(PageContext);
  const [isBrowserMobile, setIsBrowserMobile] = useState(true);
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  useEffect(() => {
    // only set value from useMediaQuery in useEffect to avoid hydration error.
    setIsBrowserMobile(isMobile);
  }, [isMobile]);
  const { t } = useTranslation();
  const currentLanguageCode = correctLocaleCasing(slug?.[1]);
  const currentLanguageName = getLanguageCodeFromKey(currentLanguageCode as LanguageCode)?.localTitle;
  const showTranslationmenu = languages.length > 1;
  const handleClosePopup = () => {
    const expandedPopup = document.querySelector(`.${styles.translateButton}[aria-expanded="true"]`) as HTMLElement;
    if (expandedPopup) {
      expandedPopup.click();
    }
  };
  if (!showTranslationmenu) return null;
  return (
    <Popover
      trigger={
        <button type="button" aria-label={t('icons.translate')} className={styles.translateButton}>
          <Icon name="translate" classname={styles.icon} />
          {fullsize && !isBrowserMobile && (
            <>
              {currentLanguageName}
              <Icon name="chevron-down" classname={styles.icon} />
            </>
          )}
        </button>
      }
      content={
        <>
          {languages.map((lang) => {
            const languageLabel = getLanguageCodeFromKey(lang as LanguageCode)?.localTitle;
            if (!languageLabel) return null;
            if (!slug) return null;
            const newSlug = [...slug];
            newSlug[1] = lang;
            const href = buildPublicPathFromSlug(newSlug, linkHostname);
            return (
              <Link
                href={href}
                key={lang}
                className={styles.languageOption}
                type="button"
                aria-label={languageLabel}
                onClick={handleClosePopup}
              >
                {languageLabel}
              </Link>
            );
          })}
        </>
      }
      classname={styles.translatePopover}
    />
  );
}
